// İSG Portal — Tabs (panel switcher). YENİ (S26 plan D14): depoda yeniden kullanılabilir bir
// sekme bileşeni yoktu — `ContractorDetailPage.tsx`/`PermitDetailPage.tsx` (web-expert) bayt
// bayt aynı satır içi `tabButton()` kopyalarıydı, seçili sekme etiketini `bg-primary/10
// text-primary` ile boyuyordu (açık temada sayfa arka planına karşı **2,04:1** — AA'nın altında
// ve seçili sekme satırdaki EN SESSİZ öğe oluyordu, hiyerarşi ters). Bu bileşen S26 personel
// profili sayfasının TEK tüketicisidir (D14) — var olan altı satır içi kopya BU dilimde
// taşınmaz (kapsam dışı, `BACKLOG.md`).
//
// **Seçili durum (S25-D9/`AnalysisInboxRail.tsx` emsali).** `bg-surface-muted text-fg
// font-semibold border border-border` — hem açık hem koyu temada 12,9:1/15,1:1 (metin/arkaplan)
// ve dolgu farkı tek başına görünmez olduğundan (`bg-surface-muted` `bg-bg` üzerinde 1,12:1
// açık / 1,30:1 koyu) `border` DEKORASYON DEĞİL, seçimi taşıyan üç sinyalden biridir (renk
// adımı + `font-semibold` + kenarlık). `border-b-2 border-primary` gibi TEK turuncu bir işaret
// KULLANILMAZ (turuncu/kum 2,23:1, 1.4.11'in state-indicator eşiği olan 3:1'i geçmez).
//
// **Erişilebilirlik — tam APG tabs sözleşmesi, bir kez burada yazılır.** Yarım uygulanmış bir
// `role="tab"` bu depoda zaten reddedildi (`AnalysisInboxRail.tsx:8-11`, `role="tree"` için
// aynı gerekçe): `role="tablist"` + her sekme `role="tab"`/`aria-selected`/`aria-controls`,
// **roving tabindex** (seçili `tabIndex=0`, diğerleri `-1`), `←`/`→` odağı TAŞIR ve SEÇER
// (otomatik etkinleştirme — panel ucuzdur, ikisi de zaten önbellekli sorgudan monte), `Home`/
// `End` uçlara atlar, panel `role="tabpanel"` + `aria-labelledby` + `tabIndex=0` (klavyeyle
// ulaşılabilir + ekran okuyucuya duyurulur). `Esc` HİÇBİR ŞEY yapmaz — burada kapatılabilir bir
// katman yok, eklenmemelidir.

import type { KeyboardEvent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Tag } from './Tag';
import { cn } from './cn';

export interface TabItem {
  value: string;
  label: ReactNode;
  /** Sessiz sayı rozeti; `count <= 0` iken HİÇBİR ŞEY render edilmez. `srLabel` ekran okuyucu
   * metni (çağıran i18n'den çözer, ör. "3 kişi hazır değil"). `"<label>, "` den SONRA okunur;
   * bu yüzden virgülden sonra doğal akmalıdır. */
  badge?: { count: number; srLabel: string };
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  /** Sekme çubuğunun erişilebilir adı (`aria-label`). */
  ariaLabel: string;
  /** `tab`/`panel` id'lerini türetmek için önek — sayfa başına benzersiz olmalı. */
  idPrefix: string;
  className?: string;
}

export function Tabs({ items, value, onValueChange, ariaLabel, idPrefix, className }: TabsProps) {
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // S33 planı §6 Slice 3 "DE tab overflow": taşan (carbon: `overflow-x-auto`) sekme listesinde
  // seçili sekme mount'ta VE her değişiminde görünüre kaydırılır. `inline`/`block: 'nearest'` —
  // yalnız YATAY kaydırma (liste kendi kabında), sayfa dikey scroll'unu ETKİLEMEZ. Liste zaten
  // sığıyorsa (base derleme, `overflow-x-auto` yok) no-op — web-supplier'ın tek tüketicisi için
  // davranış değişmez.
  useEffect(() => {
    buttonRefs.current.get(value)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  function focusTab(target: string) {
    buttonRefs.current.get(target)?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = items.findIndex((item) => item.value === value);
    if (index === -1) {
      return;
    }
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIndex = (index + 1) % items.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (index - 1 + items.length) % items.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = items.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    const next = items[nextIndex];
    if (!next) {
      return;
    }
    onValueChange(next.value);
    focusTab(next.value);
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex items-center gap-1 border-b border-border pb-2',
        // carbon: (Stage 2 "Tabs") — 40px alt-çizgi listesi, dolgu kalkar; taşan sekme
        // listesi (7 sekmeli permit-detail, de dilinde) kendi içinde kaydırılır, sarmaz
        // (plan §6 Slice 3'ün "DE tab overflow" notunun primitif-seviyesi karşılığı).
        // Kaydırma çubuğu GÖRSEL OLARAK gizli (coordinator bulgusu — 2a'da görünmüyor) ama
        // `overflow-x-auto` + yukarıdaki `scrollIntoView` DAVRANIŞI aynen kalır; yalnız
        // ÇUBUĞUN kendisi (Firefox: `scrollbar-width`, WebKit/Blink: `::-webkit-scrollbar`)
        // gizlenir, klavye/dokunma kaydırması etkilenmez.
        'carbon:h-10 carbon:gap-0 carbon:overflow-x-auto carbon:whitespace-nowrap carbon:pb-0',
        'carbon:[scrollbar-width:none] carbon:[-ms-overflow-style:none] carbon:[&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              if (el) {
                buttonRefs.current.set(item.value, el);
              } else {
                buttonRefs.current.delete(item.value);
              }
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.value}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${item.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange(item.value)}
            className={cn(
              // `relative`: sr-only rozet metni (absolute) içeren bloğunu BU düğmede bulur; yoksa
              // kaydırılan sekme listesinin overflow kırpmasından kaçıp sayfaya yatay kaydırma ekler.
              'relative inline-flex h-9 items-center rounded-md px-3 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              // carbon: pil (arka plan dolgusu) yerine 2px alt-çizgi; mavi/beyaz ~5:1
              // (1.4.11 üzerinde, turuncu/kum'un 2,23:1'inin YERİNİ alır — plan §5.3).
              // `font-semibold` İKİNCİ sinyal olarak KALIR (renk TEK BAŞINA taşımaz).
              'carbon:h-10 carbon:shrink-0 carbon:rounded-none carbon:border-0 carbon:border-b-2 carbon:border-b-transparent carbon:px-4 carbon:text-muted',
              selected
                ? 'border border-border bg-surface-muted font-semibold text-fg carbon:border-b-primary carbon:bg-transparent carbon:font-semibold carbon:text-fg'
                : 'border border-transparent text-muted hover:bg-surface-muted hover:text-fg carbon:hover:border-b-border-strong carbon:hover:bg-transparent carbon:hover:text-fg',
            )}
          >
            {item.label}
            {item.badge && item.badge.count > 0 ? (
              <>
                {/* Görsel sayı ekran okuyucudan gizli; anlamı sr-only metin taşır (renk/rakam tek
                    başına değil). Düğmenin erişilebilir adı: "<etiket>, <srLabel>". */}
                <Tag color="gray" aria-hidden="true" className="ml-2 tnum">
                  {item.badge.count}
                </Tag>
                <span className="sr-only">, {item.badge.srLabel}</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  value: string;
  activeValue: string;
  idPrefix: string;
  children: ReactNode;
  className?: string;
  /**
   * `true` ise etkin olmayan panel UNMOUNT edilmez — native `hidden` özniteliğiyle DOM'da kalır.
   * `hidden`, `display:none` ile eşdeğerdir: alt ağacı hem render hem erişilebilirlik ağacından
   * HEM tab sırasından çıkarır — CSS `visibility`/`opacity` gibi yarım bir gizleme değildir, yani
   * APG tabpanel sözleşmesini bozmaz. Sekmeler arası geçişte silinmemesi gereken yerel state taşıyan
   * bir tüketici için (ör. sürükle-bırak/seçili dosya durumu). Varsayılan `false` — etkin olmayan
   * panel hiç monte edilmez (önceki davranış).
   */
  keepMounted?: boolean;
}

export function TabPanel({
  value,
  activeValue,
  idPrefix,
  children,
  className,
  keepMounted = false,
}: TabPanelProps) {
  const active = value === activeValue;
  if (!active && !keepMounted) {
    return null;
  }
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${value}`}
      aria-labelledby={`${idPrefix}-tab-${value}`}
      hidden={!active}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: APG tabpanel sözleşmesi kasıtlı (dosya başlığı, "Erişilebilirlik" notu)
      tabIndex={0}
      className={cn(
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {children}
    </div>
  );
}
